/**
 * Topology edge palette — the single source of truth for the ReactFlow edge colors so they are
 * never hardcoded inline. Two visual dimensions share the same two hues:
 *  - Cluster mode colours by REFLECTOR IDENTITY: `selected` (the member shown in the switch) vs
 *    `secondary` (the other member — e.g. routes only it announces).
 *  - Single-reflector mode colours by PATH: `best` vs `alt`.
 * `withdrawn` is the tombstone-only edge treatment (single-reflector mode).
 * Mirrored as CSS custom properties in `theme/styles/themes.css` for DOM swatches (the legend);
 * the canvas edges use these JS values directly because ReactFlow marker fills don't resolve var().
 */
export const BGP_EDGE_COLORS = {
  selected: '#ff4088',
  best: '#ff4088',
  secondary: '#9CA3AF',
  alt: '#9CA3AF',
  withdrawn: '#EF4444',
  /** Routes announced by BOTH cluster members (present per reflector). Pink, to read as the
   *  "common to every reflector" hue against the brand-teal unique divergences. */
  shared: '#ff4088',
  /** Cluster mode (CTX-98): prefixes UNIQUE to the selected reflector — announced by it but not by
   *  the other member. Brand teal (`--color-semantic-bg-brand-bold`), from the selected reflector's
   *  perspective. Kept as the concrete hex, not a var(), because ReactFlow marker (arrowhead) fills
   *  don't resolve CSS custom properties (see file header).
   *  Legacy: no longer used for edge colouring since CTX-106 (teal "unique" edge removed). */
  unique: '#007d8b',
  /** Cluster mode (CTX-106): count-badge colour when the selected reflector and the other member
   *  announce a DIFFERENT number of prefixes to the same route table (an inconsistency to review).
   *  Amber; concrete hex for the same ReactFlow-fill reason as above. Applied to the edge's `×N`
   *  count label only — the line/arrow stay pink (best) / grey (alternate). */
  inconsistent: '#F59E0B'
} as const;

export const bgpVisibilityStyles = `
  .rtHandle:hover {
    opacity: 0.85 !important;
  }
  .react-flow__edges {
    z-index: 50 !important;
  }
  .react-flow__edge {
    z-index: 50 !important;
  }
  .react-flow__edge-path {
    z-index: 50 !important;
  }

  @keyframes flowPink {
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -100; }
  }

  /* Ensure ReactFlow adapts to viewport - allow internal scrolling if needed */
  .react-flow {
    width: 100% !important;
    height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }
  .react-flow__container {
    width: 100% !important;
    height: 100% !important;
    position: relative !important;
  }
  .react-flow__viewport {
    width: 100% !important;
    height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }
  .react-flow__renderer {
    width: 100% !important;
    height: 100% !important;
  }
  /* Hide scrollbars but allow scrolling via pan */
  .react-flow__pane {
    cursor: grab;
    overflow: visible !important;
  }
  .react-flow__pane.dragging {
    cursor: grabbing;
  }
`;

